<template>
  <div>
    <naj-box :title="local.bankruptcy.subRequests.subRequestObjections.objectionExchange">
      <div>
        <div
          v-for="(m, i) in subRequestOwnMemos"
          :key="m.objectKey"
          class="mb-4"
        >
          <v-row>
            <v-col cols="12">
              <naj-input
                :type-id="formFieldType.textarea"
                :label="
                  m.isReply
                    ? local.bankruptcy.subRequests.subRequestObjections.reply
                    : local.bankruptcy.subRequests.subRequestObjections.objectionText
                "
                :value="m.text || m.memoText || ''"
                :disabled="true"
                :validates="m.isReply ? 'max:2000' : 'max:10000'"
              ></naj-input>
            </v-col>
          </v-row>
          <v-row
            dense
            class="text-right"
          >
            <template v-if="m.isReply">
              <v-col
                cols="12"
                sm="2"
              >
                <div class="label">
                  {{ local.bankruptcy.subRequests.subRequestObjections.name }}
                </div>
                <div class="value">
                  <template v-if="m.isFirm">
                    {{ m.partyDetails && m.partyDetails.firmName }}
                  </template>
                  <template v-else>
                    {{
                      joinNames(
                        m.partyDetails && m.partyDetails.firstName,
                        m.partyDetails && m.partyDetails.fatherName,
                        m.partyDetails && m.partyDetails.familyName,
                      )
                    }}
                  </template>
                </div>
              </v-col>

              <v-col
                cols="12"
                sm="2"
              >
                <div class="label">
                  {{ local.bankruptcy.subRequests.subRequestObjections.role }}
                </div>
                <div class="value">
                  {{ getLegalCapacityName(m.partyDetails && m.partyDetails.legalCapacityId, legalCapacities) }}
                </div>
              </v-col>

              <v-col
                cols="12"
                sm="2"
              >
                <div class="label">
                  {{ local.bankruptcy.subRequests.subRequestObjections.idType }}
                </div>
                <div class="value">
                  {{ getIdTypeName(m.partyDetails && m.partyDetails.identityTypeId, identityTypes) }}
                </div>
              </v-col>

              <v-col
                cols="12"
                sm="2"
              >
                <div class="label">
                  {{ local.bankruptcy.subRequests.subRequestObjections.idNumber }}
                </div>
                <div class="value">
                  <template v-if="m.isFirm">
                    {{ companyIdNumber(m.partyDetails) }}
                  </template>
                  <template v-else>
                    {{ m.partyDetails && m.partyDetails.identityNumber }}
                  </template>
                </div>
              </v-col>
            </template>

            <v-col
              cols="12"
              sm="2"
            >
              <div class="label">
                {{
                  m.isReply
                    ? local.bankruptcy.subRequests.subRequestObjections.replyDate
                    : local.bankruptcy.subRequests.subRequestObjections.objectionDate
                }}
              </div>
              <div class="value">
                {{ toHijriDate(m.createDate) }}
              </div>
            </v-col>

            <v-col
              cols="12"
              sm="2"
            >
              <div class="label">
                {{
                  m.isReply
                    ? local.bankruptcy.subRequests.subRequestObjections.replyTime
                    : local.bankruptcy.subRequests.subRequestObjections.objectionTime
                }}
              </div>
              <div class="value">
                {{ getTimePartArabic(m.createDate) }}
              </div>
            </v-col>
          </v-row>
          
          <!-- Memo Attachments Section -->
          <v-row v-if="m.attachments && m.attachments.length > 0">
            <v-col cols="12">
              <div class="text-h6 mb-2">
                {{ local.attachments.title }}
              </div>
              <attachment-list 
                :attachments="m.attachments"
                :disableDelete="true"
              ></attachment-list>
            </v-col>
          </v-row>
          
          <v-divider></v-divider>
        </div>
        <v-row>
          <v-col
            v-if="canApplyObjection"
            cols="12"
          >
            <naj-input
              :type-id="formFieldType.textarea"
              :label="local.bankruptcy.subRequests.subRequestObjections.objectionText"
              :value.sync="objectionText"
              validates="max:10000|allowed_special_character|required"
            ></naj-input>
          </v-col>
        </v-row>
        <v-row v-if="canApplyObjection">
          <v-col cols="12">
            <div class="text-h6">
              {{ local.attachments.title }}
            </div>
            <attachment-list :attachments="uploadAttachments"></attachment-list>
          </v-col>
        </v-row>
        <v-row v-if="canApplyObjection">
          <v-col cols="6">
            <naj-button
              outlined
              type="button"
              color="primary"
              :disabled="!canApplyObjection"
              @click="showGeneralAttachmentModal"
              >
              {{ local.bankruptcy.subRequests.subRequestObjections.AddAttachments }}
            </naj-button>
          </v-col>
        </v-row>
      </div>
    </naj-box>
    <v-row v-if="canApplyObjection">
      <v-col
        cols="12"
        class="d-flex justify-end align-end"
      >
        <naj-button
          outlined
          @click="onCancel"
        >
          {{ local.common.cancel }}
        </naj-button>

        <naj-button
          class="ml-4"
          :disabled="!canApplyObjection || !hasObjectionText"
          @click="submitObjection"
        >
          {{ local.bankruptcy.subRequests.subRequestObjections.objectionReply }}
        </naj-button>
      </v-col>
    </v-row>
    <request-reason-modal
      :modal-visibility="showAttachmentModal"
      @addRequestReasonAttachment="handleAttachmentSubmit"
      @onClose="hideAttachmentModal"
    >
    </request-reason-modal>
    <naj-confirmation
      :show-modal="showApplyConfirm"
      :max-width="420"
      :is-persistent="true"
      :title="''"
      :show-close-btn="false"
      @cancel="showApplyConfirm = false"
    >
      <template slot="message-content">
        <h3 class="success-wrap">
          <v-icon
            size="75"
            color="var(--app-primary-color)"
          >
            mdi-check-circle
          </v-icon>
          <div class="success-text">
            {{ local.bankruptcy.subRequests.subRequestObjections.objectionApplyConfirmMessage }}
          </div>
        </h3>
      </template>

      <template slot="actions">
        <naj-button
          class="full-width"
          @click="handleSuccessBack"
        >
          {{ local.bankruptcy.subRequests.subRequestObjections.back }}
        </naj-button>
      </template>
    </naj-confirmation>
  </div>
</template>

<script>
import NajConfirmation from '@/components/shared/naj-confirmation.vue';
import LoadLocalAttachment from '@/components/shared/attachment-modal/load-local-attachment.vue';
import AttachmentList from './objections-tab/attachments-list.vue';
import { createNamespacedHelpers } from 'vuex';
import { toHijriDate } from '@/helpers/utils';
import { getTimePartArabic } from '@/helpers/utils';
import { getIdTypeName, getLegalCapacityName, joinNames } from '../../helpers/utils';
const { mapState: mapGlobalState } = createNamespacedHelpers('GlobalModule');
const { mapState: mapLUTState, mapActions: mapBankruptcyActions } = createNamespacedHelpers('BankruptcyModule');
const { mapActions: mapCasesActions } = createNamespacedHelpers('CasesModule');
import RequestReasonModal from '@/components/shared/request-reason-modal/index';

export default {
  name: 'ObjectionForm',
  components: { 
    NajConfirmation,
    LoadLocalAttachment,
    AttachmentList,
    RequestReasonModal,
  },
  props: {
    subRequestObjectKey: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      objectionText: '',
      showApplyConfirm: false,
      canApplyObjection: false,
      subRequestOwnMemos: [],
      uploadAttachments: [],
      maxFileSize: 5,
      loading: false,
      showAttachmentModal: false,
    };
  },
  computed: {
    ...mapGlobalState(['userInfo']),
    ...mapLUTState(['identityTypes', 'legalCapacities']),
    hasObjectionText() {
      const t = this.objectionText.trim();
      return t.length > 0 && t.length <= 10000;
    },
  },
  created() {
    this.getIdentityTypes();
    this.getLegalCapacities();
    this.getCanApplyObjection();
    this.getSubRequestOwnMemos();
  },
  methods: {
    ...mapBankruptcyActions([
      'submitSubRequestMemo',
      'getOwnSubRequestMemo',
      'getIdentityTypes',
      'getLegalCapacities',
      'canIssueSubRequestMemo',
    ]),
    ...mapCasesActions(['uploadAttachment']),
    async submitObjection() {
      if (!this.canApplyObjection || !this.hasObjectionText) return;

      try {
        const payload = {
          subRequestObjectKey: this.subRequestObjectKey,
          text: this.objectionText.trim(),
          attachments: this.uploadAttachments,
        };

        if (this.subRequestOwnMemos && this.subRequestOwnMemos.length > 0) {
          const last = this.subRequestOwnMemos[this.subRequestOwnMemos.length - 1];

          if (last && last.objectKey) {
            payload.parentMemoObjectKey = last.objectKey;
          }
        }

        await this.submitSubRequestMemo(payload);

        this.objectionText = '';
        this.uploadAttachments = [];
        this.showApplyConfirm = true;
        this.getSubRequestOwnMemos(this.subRequestObjectKey);
        this.getCanApplyObjection();
      } catch (err) {
        this.toaster?.error?.(err?.message || this.local.common.generalError);
      }
    },
    onCancel() {
      this.objectionText = '';
      this.uploadAttachments = [];
      this.$router.push({ path: `/bankruptcy` });
    },
    handleSuccessBack() {
      this.showApplyConfirm = false;
      this.$router.push({ path: `/bankruptcy` });
    },
    companyIdNumber(pd) {
      if (!pd) return '—';
      return pd.unifiedNumber || pd.commercialRegistrationNumber || '—';
    },
    getCanApplyObjection() {
      this.canIssueSubRequestMemo(this.subRequestObjectKey).then((res) => {
        this.canApplyObjection = res.data;
      });
    },
    getSubRequestOwnMemos() {
      this.getOwnSubRequestMemo(this.subRequestObjectKey).then((res) => {
        this.subRequestOwnMemos = res.data;
      });
    },
    hideAttachmentModal() {
      this.showAttachmentModal = false;
    },
    showGeneralAttachmentModal() {
      this.showAttachmentModal = true;
    },
    async handleAttachmentSubmit(attachment) {
      const displayedAttachment = {
        fileId: attachment.sharePointId || attachment.fileId,
        fileName: attachment.sharePointFileName || attachment.fileName || attachment.name,
        extension: attachment.extension,
        contentType: attachment.fileType,
        fileTypeId: attachment.fileTypeId,
      };
      if (!this.uploadAttachments) this.uploadAttachments = [];
      this.uploadAttachments.push(displayedAttachment);
      this.$set(this, 'uploadAttachments', this.uploadAttachments);
      this.hideAttachmentModal();
    },
    toHijriDate,
    getTimePartArabic,
    getLegalCapacityName,
    getIdTypeName,
    joinNames,
  },
};
</script>

<style>
.success-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 0 0 4px;
  text-align: center;
}

.success-text {
  font-size: 18px;
  font-weight: 600;
  color: #6b7280;
}

.v-button-wrapper.full-width button {
  width: 100%;
}

.full-width {
  width: 100%;
}

.label {
  display: inline;
  margin-left: 6px;
  color: #6b7280;
}

.value {
  display: inline;
  font-weight: 600;
}
</style>